import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { User, Mail, Phone, Lock, Eye, EyeOff, XCircle, ArrowRight } from "lucide-react";
import { controlLoading } from "@/services/slices/app.slice";
import { sendEmailVerificationCodeAPI } from "@/services/api/user/signup";
import Modal from "@/components/Modal";
import {
  AUTH_CARD_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
  AUTH_SELECT_CLASS,
  authInputClass,
} from "@/components/auth/authStyles";
import { COUNTRY_CODES } from "@/assets/countryCodes";
import { buildSignupQuery, readSignupParams } from "./signupState";

const VALID_DOMAINS = [
  "hku",
  "hkust",
  "ust",
  "cuhk",
  "polyu",
  "hkbu",
  "cityu",
  "eduhk",
  "hksyu",
  "hkmu",
  "lingu",
  "ln",
  "hkuspace",
  "csmu",
  "gmail",
];

type FieldKey =
  | "username"
  | "email"
  | "phoneNum"
  | "password"
  | "confirmedPassword";

/** Focus order for the inline-error pass — also the visual field order. */
const FIELD_ORDER: FieldKey[] = [
  "username",
  "email",
  "phoneNum",
  "password",
  "confirmedPassword",
];

/**
 * `phoneNum` travels as `${countryCode} ${phoneNum}` (e.g. "+852 53968435"),
 * so BACK from the verification step has to be split back into the two inputs.
 * An unrecognised code falls back to the default rather than rendering a
 * <select> with no matching option.
 */
const splitPhoneParam = (value?: string) => {
  const [code, ...rest] = (value || "").trim().split(" ");
  return {
    countryCode: COUNTRY_CODES.some((c) => c.value === code) ? code : "+852",
    phoneNum: rest.join(" "),
  };
};

/**
 * Signup step 1 — account details. Ports the RN `useSignupForm` validation
 * and calls `sendEmailVerificationCodeAPI` before moving to the OTP step.
 * Returning from step 2 puts the whole query back on the URL, which is what
 * rehydrates the form here.
 */
const SignupPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const initial = readSignupParams(search);
  const initialPhone = splitPhoneParam(initial.phoneNum);

  const [username, setUsername] = useState(initial.username || "");
  const [email, setEmail] = useState(initial.email || "");
  const [phoneNum, setPhoneNum] = useState(initialPhone.phoneNum);
  const [countryCode, setCountryCode] = useState(initialPhone.countryCode);
  const [password, setPassword] = useState(initial.password || "");
  const [confirmedPassword, setConfirmedPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [modalAlertVisible, setModalAlertVisible] = useState(false);
  const [modalAlertText, setModalAlertText] = useState("");

  const usernameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneNumRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmedPasswordRef = useRef<HTMLInputElement>(null);

  const FIELD_REFS: Record<FieldKey, React.RefObject<HTMLInputElement>> = {
    username: usernameRef,
    email: emailRef,
    phoneNum: phoneNumRef,
    password: passwordRef,
    confirmedPassword: confirmedPasswordRef,
  };

  const showAlert = (text: string) => {
    setModalAlertText(t(text));
    setModalAlertVisible(true);
  };

  /** Typing into a field clears its inline error so the form feels responsive. */
  const handleChange = (
    key: FieldKey,
    value: string,
    setter: (value: string) => void,
  ) => {
    setter(value);
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleRegister = async () => {
    const errors: Partial<Record<FieldKey, string>> = {};
    if (!username) errors.username = "Please fill in all the blank";
    if (!email) errors.email = "Please fill in all the blank";
    if (!phoneNum.trim()) errors.phoneNum = "Please fill in all the blank";
    if (!password) errors.password = "Please fill in all the blank";
    if (!confirmedPassword) errors.confirmedPassword = "Please fill in all the blank";
    if (email) {
      const domain = email.split("@")[1] || "";
      if (!VALID_DOMAINS.some((d) => domain.includes(d))) {
        errors.email = "Please use university email";
      }
    }
    if (password && password.length < 8) {
      errors.password = "Password must be at least 8 digits!";
    }
    if (password && confirmedPassword && password !== confirmedPassword) {
      errors.confirmedPassword = "Password and Confirmed Password are not match";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstInvalid = FIELD_ORDER.find((key) => errors[key]);
      if (firstInvalid) FIELD_REFS[firstInvalid].current?.focus();
      return;
    }
    setFieldErrors({});
    dispatch(controlLoading({ showLoading: true }));
    const resp = await sendEmailVerificationCodeAPI(username, email);
    dispatch(controlLoading({ showLoading: false }));
    if (resp.status === -20000) return showAlert("signup.emailExisted");
    if (resp.status === -20001) return showAlert("signup.usernameExisted");
    if (resp.status === -20002) return showAlert("signup.invalidUniversityEmail");
    if (resp.status === 0) {
      navigate(
        `/signup/verify${buildSignupQuery({
          username,
          email,
          phoneNum: `${countryCode} ${phoneNum.trim()}`,
          password,
        })}`,
      );
    }
  };

  const fieldError = (key: FieldKey) => {
    const message = fieldErrors[key];
    if (!message) return null;
    return (
      <p className="mt-1.5 flex items-center gap-1 text-[13px] text-red-500">
        <XCircle size={14} className="shrink-0" />
        {t(message)}
      </p>
    );
  };

  return (
    <div className={`${AUTH_CARD_CLASS} overflow-hidden`}>
      {/* Header — same heading block the login page uses. */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#333]">{t("Signup")}</h1>
        <p className="mt-1.5 text-sm text-[#333]/45">
          {t("Create your account with your university email.")}
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="signup-username"
            className={AUTH_LABEL_CLASS}
          >
            {t("Username")}
          </label>
          <div className="relative">
            <User
              size={18}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="signup-username"
              ref={usernameRef}
              type="text"
              value={username}
              autoComplete="username"
              onChange={(e) => handleChange("username", e.target.value, setUsername)}
              className={authInputClass(!!fieldErrors.username)}
              placeholder={t("Username")}
              aria-invalid={!!fieldErrors.username}
            />
          </div>
          {fieldError("username")}
        </div>
        <div>
          <label
            htmlFor="signup-email"
            className={AUTH_LABEL_CLASS}
          >
            {t("Your University Email")}
          </label>
          <div className="relative">
            <Mail
              size={18}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="signup-email"
              ref={emailRef}
              type="email"
              value={email}
              autoComplete="email"
              onChange={(e) => handleChange("email", e.target.value, setEmail)}
              className={authInputClass(!!fieldErrors.email)}
              placeholder="you@connect.hku.hk"
              aria-invalid={!!fieldErrors.email}
            />
          </div>
          {fieldError("email")}
        </div>
        <div>
          <label
            htmlFor="signup-phone"
            className={AUTH_LABEL_CLASS}
          >
            {t("Phone Number")}
          </label>
          <div className="flex gap-2">
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className={`${AUTH_SELECT_CLASS} w-28`}
              autoComplete="tel-country-code"
              aria-label={t("Region Code")}
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.flag} {c.value}
                </option>
              ))}
            </select>
            <div className="relative flex-1">
              <Phone
                size={18}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                id="signup-phone"
                ref={phoneNumRef}
                type="tel"
                value={phoneNum}
                autoComplete="tel-national"
                onChange={(e) => handleChange("phoneNum", e.target.value, setPhoneNum)}
                className={authInputClass(!!fieldErrors.phoneNum)}
                placeholder="0000 0000"
                aria-invalid={!!fieldErrors.phoneNum}
              />
            </div>
          </div>
          {fieldError("phoneNum")}
        </div>
        <div>
          <label
            htmlFor="signup-password"
            className={AUTH_LABEL_CLASS}
          >
            {t("Password")}
          </label>
          <div className="relative">
            <Lock
              size={18}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="signup-password"
              ref={passwordRef}
              type={showPassword ? "text" : "password"}
              value={password}
              autoComplete="new-password"
              onChange={(e) => handleChange("password", e.target.value, setPassword)}
              className={authInputClass(!!fieldErrors.password, true)}
              placeholder="••••••••"
              aria-invalid={!!fieldErrors.password}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t("Hide password") : t("Show password")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-[#ffa217]"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {fieldError("password")}
        </div>
        <div>
          <label
            htmlFor="signup-confirmed-password"
            className={AUTH_LABEL_CLASS}
          >
            {t("Confirmed Password")}
          </label>
          <div className="relative">
            <Lock
              size={18}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="signup-confirmed-password"
              ref={confirmedPasswordRef}
              type={showConfirmedPassword ? "text" : "password"}
              value={confirmedPassword}
              autoComplete="new-password"
              onChange={(e) =>
                handleChange("confirmedPassword", e.target.value, setConfirmedPassword)
              }
              onKeyDown={(e) => e.key === "Enter" && handleRegister()}
              className={authInputClass(!!fieldErrors.confirmedPassword, true)}
              placeholder="••••••••"
              aria-invalid={!!fieldErrors.confirmedPassword}
            />
            <button
              type="button"
              onClick={() => setShowConfirmedPassword((v) => !v)}
              aria-label={
                showConfirmedPassword ? t("Hide password") : t("Show password")
              }
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-[#ffa217]"
            >
              {showConfirmedPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {fieldError("confirmedPassword")}
        </div>

        <button
          type="button"
          onClick={handleRegister}
          className={`group flex items-center justify-center gap-2 ${AUTH_PRIMARY_BUTTON_CLASS}`}
        >
          {t("REGISTER")}
          <ArrowRight
            size={16}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </button>
      </div>

      <Modal visible={modalAlertVisible} onClose={() => setModalAlertVisible(false)}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mb-4 text-lg font-semibold text-[#333]">{modalAlertText}</div>
          <button
            type="button"
            onClick={() => setModalAlertVisible(false)}
            className="rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default SignupPage;
